import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./HeadroomPaywall.module.scss"

/**
 * Headroom — a feature-specific paywall for the Cloud Vault feature.
 *
 * The mechanic is a rotary "keep dial": your phone is out of room, so today you
 * have to choose what to delete. Turn the dial and your private Cloud Vault
 * opens — every memory lifts off the cramped phone and settles into an
 * ever-taller private room, kept in full and only yours. The screen breathes
 * open from tension (a full phone) to relief (endless private headroom).
 *
 * Divergence: rotary-dial interaction, an architectural "room that fills"
 * spatial layout, a cramped→spacious emotional arc, and a value frame of
 * "room to keep everything, privately" rather than declutter or fear.
 *
 * Self-contained: renders on the built-in debug product data, reuses the
 * bundled `nimbus/` memory photos, no native injection required.
 */

// Real everyday memories, reused from the bundled photo set so the screen is
// fully self-contained (no external fetches at runtime). Masonry heights give
// the room a real, mixed camera-roll feel instead of a uniform matrix.
const MEMORIES: { file: string; span: 1 | 2 }[] = [
  { file: "mem2.jpg", span: 1 },
  { file: "mem4.jpg", span: 2 },
  { file: "mem1.jpg", span: 2 },
  { file: "mem11.jpg", span: 1 },
  { file: "mem7.jpg", span: 1 },
  { file: "mem9.jpg", span: 1 },
  { file: "mem6.jpg", span: 2 },
  { file: "mem14.jpg", span: 1 },
  { file: "mem5.jpg", span: 1 },
  { file: "mem12.jpg", span: 2 },
  { file: "mem10.jpg", span: 1 },
  { file: "mem8.jpg", span: 1 },
  { file: "mem16.jpg", span: 2 },
  { file: "mem3.jpg", span: 1 },
  { file: "mem13.jpg", span: 1 },
  { file: "mem15.jpg", span: 1 },
]

// Illustrative library size (not a claim) — the count the vault keeps once the
// dial is all the way up. The phone can only hold a cramped handful.
const LIBRARY = 1268
const PHONE_FITS = 46
const VAULT_GB = 50 // real Premium Cloud Vault size
const GB_AT_FULL = 41.6 // how full the private cloud reads when everything's kept

// Dial sweep: a 270° gauge, dead zone at the bottom. -135° = empty, +135° = all.
const SWEEP = 270
const HALF = SWEEP / 2

function clamp01(v: number) {
  return Math.max(0, Math.min(1, v))
}

const LockGlyph = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect
      x="4.5"
      y="10.6"
      width="15"
      height="10.4"
      rx="3"
      fill="currentColor"
    />
    <path
      d="M8 10.6V7.7a4 4 0 0 1 8 0v2.9"
      stroke="currentColor"
      strokeWidth="2.1"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
)

const HeadroomPaywall = () => {
  const { state, actions } = useAppState()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true
  const trialDays = yearlyProduct?.introPricing?.period ?? 7
  const savePercent =
    yearlyProduct && monthlyProduct && monthlyProduct.price > 0
      ? Math.max(
          0,
          Math.round(
            (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
          ),
        )
      : 79

  const darkMode = state.initialData?.darkMode ?? false

  const [selected, setSelected] = useState<"yearly" | "monthly">("yearly")
  const [t, setT] = useState(0)

  const dialRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)
  const touchedRef = useRef(false)
  const startedRef = useRef(false)

  // Tint the OS inset bars to blend with the light room.
  useEffect(() => {
    actions.setHeaderInsetColor(darkMode ? "#181c25" : "#fbfcfe")
    actions.setFooterInsetColor(darkMode ? "#171a22" : "#e9edf5")
  }, [actions, darkMode])

  // One-time teaching sweep: the dial glides open on its own so the mechanic is
  // obvious, settling part-way and inviting the user to take it the rest.
  useLayoutEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    const target = 0.68
    const duration = 1750
    let raf = 0
    let startTs = 0
    const ease = (x: number) => 1 - Math.pow(1 - x, 3)
    const tick = (ts: number) => {
      if (touchedRef.current) return
      if (!startTs) startTs = ts
      const p = Math.min(1, (ts - startTs) / duration)
      setT(ease(p) * target)
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    const kickoff = window.setTimeout(() => {
      raf = requestAnimationFrame(tick)
    }, 560)
    return () => {
      window.clearTimeout(kickoff)
      cancelAnimationFrame(raf)
    }
  }, [])

  const setFromPointer = useCallback((clientX: number, clientY: number) => {
    const el = dialRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const cx = r.left + r.width / 2
    const cy = r.top + r.height / 2
    // Angle measured from 12 o'clock, clockwise positive. Up = 0, right = +90,
    // left = -90, straight down = ±180.
    const deg = (Math.atan2(clientX - cx, cy - clientY) * 180) / Math.PI
    let value: number
    if (deg > HALF) {
      value = deg > 175 ? 0 : 1 // bottom dead zone: right half snaps to full
    } else if (deg < -HALF) {
      value = 0 // left of the dead zone snaps to empty
    } else {
      value = (deg + HALF) / SWEEP
    }
    setT(clamp01(value))
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      touchedRef.current = true
      draggingRef.current = true
      ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
      setFromPointer(e.clientX, e.clientY)
    },
    [setFromPointer],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!draggingRef.current) return
      setFromPointer(e.clientX, e.clientY)
    },
    [setFromPointer],
  )

  const endDrag = useCallback(() => {
    draggingRef.current = false
  }, [])

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      touchedRef.current = true
      setT((v) => clamp01(v + 0.08))
      e.preventDefault()
    } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      touchedRef.current = true
      setT((v) => clamp01(v - 0.08))
      e.preventDefault()
    }
  }, [])

  const kept = Math.round(t * LIBRARY)
  const gb = t * GB_AT_FULL
  const revealed = Math.round(t * MEMORIES.length)
  const sealed = t > 0.985
  const dialAngle = -HALF + t * SWEEP

  // Gauge arc geometry (270° track with the gap at the bottom).
  const R = 52
  const CIRC = 2 * Math.PI * R
  const trackLen = (SWEEP / 360) * CIRC
  const gapLen = CIRC - trackLen

  const buy = useCallback(() => {
    const product = selected === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }, [selected, yearlyProduct, monthlyProduct])

  const close = useCallback(() => native.postNativeEvent("close"), [])

  const ctaLabel =
    selected === "yearly" && hasTrial
      ? `Try ${trialDays} days free`
      : "Keep everything"

  const readout = useMemo(() => {
    if (t < 0.01) return "Your phone is full — something has to go."
    if (sealed) return "Every memory kept — private, in full, only yours."
    return "Lifting your memories into the vault…"
  }, [t, sealed])

  return (
    <div className={`${styles.page} ${sealed ? styles.isSealed : ""}`}>
      <button
        className={styles.close}
        onClick={close}
        aria-label="Not now"
        style={{ top: 10 }}
      >
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m6 6 12 12M18 6 6 18"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.intro}>
        <span className={styles.kicker}>
          <LockGlyph className={styles.kickerLock} />
          Private Photo Vault
        </span>
        <h1 className={styles.headline}>
          Never delete a memory
          <br />
          to make room again.
        </h1>
        <p className={styles.sub}>
          Your phone fills up. Your <strong>Cloud&nbsp;Vault</strong>{" "}
          doesn&apos;t — keep it all, encrypted and only yours.
        </p>
      </header>

      {/* The room: it grows taller and fills with memories as the dial turns. */}
      <section className={styles.room} aria-hidden="true">
        <div className={styles.roomGlow} style={{ opacity: 0.25 + t * 0.75 }} />
        <div
          className={styles.ceiling}
          style={{ transform: `translateY(${(1 - t) * 34}px)` }}
        >
          <span className={styles.ceilingCap}>{VAULT_GB} GB private cloud</span>
        </div>

        <div className={styles.masonry}>
          {MEMORIES.map((m, i) => {
            const on = i < revealed
            return (
              <figure
                key={m.file}
                className={`${styles.tile} ${m.span === 2 ? styles.tall : ""} ${
                  on ? styles.tileOn : ""
                }`}
                style={{ transitionDelay: `${(i % 6) * 34}ms` }}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/nimbus/${m.file}`}
                  alt=""
                  loading="eager"
                  draggable={false}
                />
                <span className={styles.tileLock}>
                  <LockGlyph />
                </span>
              </figure>
            )
          })}
        </div>

        {/* An opaque floor so memories read as continuing behind it (no hard crop). */}
        <div className={styles.floor} aria-hidden="true" />

        {/* The cramped phone at the room's threshold — the memories lift off it. */}
        <div className={styles.phone} style={{ opacity: 0.2 + (1 - t) * 0.72 }}>
          <div className={styles.phoneScreen}>
            <span className={styles.phoneFull}>Storage Full</span>
            <div className={styles.phoneBar}>
              <i style={{ width: `${90 - t * 6}%` }} />
            </div>
            <span className={styles.phoneNote}>only {PHONE_FITS} fit</span>
          </div>
        </div>
      </section>

      {/* The dial + live count. Turning it keeps everything. */}
      <section className={styles.control}>
        <div className={styles.count}>
          <span className={styles.countNum}>
            {kept.toLocaleString()}
            <span className={styles.countOf}>
              {" "}
              / {LIBRARY.toLocaleString()}
            </span>
          </span>
          <span className={styles.countLabel}>
            memories kept · {gb.toFixed(1)} of {VAULT_GB} GB
          </span>
        </div>

        <div
          ref={dialRef}
          className={styles.dial}
          role="slider"
          aria-label="Turn to keep every memory in your Cloud Vault"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(t * 100)}
          tabIndex={0}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={onKeyDown}
        >
          <svg viewBox="0 0 120 120" className={styles.gauge}>
            <circle
              className={styles.gaugeTrack}
              cx="60"
              cy="60"
              r={R}
              strokeDasharray={`${trackLen} ${gapLen}`}
            />
            <circle
              className={styles.gaugeFill}
              cx="60"
              cy="60"
              r={R}
              strokeDasharray={`${t * trackLen} ${CIRC}`}
            />
          </svg>
          <div className={styles.dialFace}>
            <LockGlyph className={styles.dialLock} />
            <span className={styles.dialWord}>{sealed ? "KEPT" : "KEEP"}</span>
          </div>
          <div
            className={styles.dialGrip}
            style={{ transform: `rotate(${dialAngle}deg)` }}
          >
            <span />
          </div>
        </div>

        <p className={styles.readout}>{readout}</p>
      </section>

      {/* Pricing — yearly featured, monthly demoted. */}
      <section className={styles.pricing} style={{ paddingBottom: 12 }}>
        <button
          className={`${styles.yearCard} ${
            selected === "yearly" ? styles.yearActive : ""
          }`}
          onClick={() => setSelected("yearly")}
          aria-pressed={selected === "yearly"}
        >
          <span className={styles.yearBadge}>
            BEST VALUE · SAVE {savePercent}%
          </span>
          <span className={styles.yearMain}>
            <span className={styles.yearName}>Yearly</span>
            <span className={styles.yearPrice}>{yearlyPrice}</span>
          </span>
          <span className={styles.yearSub}>
            <span>Just {yearlyPerMonth}/mo, billed yearly</span>
            <span>
              {hasTrial ? `${trialDays}-day free trial · ` : ""}
              {VAULT_GB} GB Cloud Vault
            </span>
          </span>
          <span className={styles.radio} aria-hidden="true" />
        </button>

        <button
          className={`${styles.monthLine} ${
            selected === "monthly" ? styles.monthActive : ""
          }`}
          onClick={() => setSelected("monthly")}
          aria-pressed={selected === "monthly"}
        >
          Or stay flexible — monthly at <strong>{monthlyPrice}/mo</strong>
        </button>

        <button className={styles.cta} onClick={buy}>
          <span>{ctaLabel}</span>
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M5 12h13m0 0-5-5m5 5-5 5"
              stroke="currentColor"
              strokeWidth="2.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <p className={styles.fine}>
          {selected === "yearly" && hasTrial
            ? `${trialDays} days free, then ${yearlyPrice}/year. `
            : selected === "yearly"
              ? `${yearlyPrice}/year. `
              : `${monthlyPrice}/month. `}
          Auto-renews until canceled.
        </p>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </section>
    </div>
  )
}

export default HeadroomPaywall
